/**
 * The five categories of the HNWIs page band, and the type labels each stands
 * for (#553).
 *
 * Shared, and that is the whole point: the tile's count query and the filter the
 * tile applies read THIS table. They lived only in the frontend's
 * `quick-filter-defs.tsx`, so a count computed server-side would have been a
 * second copy, and two copies of a mapping are two mappings, one of which is
 * wrong the first time a label changes.
 */

export const PROFILE_CATEGORY_KEYS = [
    "all",
    "exit_entrepreneurs",
    "business_angels",
    "family_offices",
    "c_levels",
] as const;

export type ProfileCategoryKey = (typeof PROFILE_CATEGORY_KEYS)[number];

/**
 * Which type labels belong to each category.
 *
 * `all` maps to nothing: it is the whole licence, not a set of labels.
 *
 * Several labels per category is real: "Family Offices" covers both `FO` and
 * `SFO`, so this is a list, never a single string.
 *
 * The labels must match `profile_types` exactly. They are the strings the
 * classification engine writes into `profiles.types`, and a near-miss here
 * produces a tile that counts zero while its tab shows rows.
 */
export const PROFILE_CATEGORY_TYPES: Record<Exclude<ProfileCategoryKey, "all">, readonly string[]> = {
    exit_entrepreneurs: ["Exit entrepreneur"],
    business_angels: ["Business Angels", "BA"],
    family_offices: ["FO", "SFO"],
    c_levels: ["C-level"],
};

/** Every label any category claims: the "is this profile categorised at all" test. */
export const ALL_CATEGORY_TYPES: readonly string[] = Object.values(PROFILE_CATEGORY_TYPES).flat();

/** The four real categories: `all` is the total, not one of them. */
export type ProfileCategory = Exclude<ProfileCategoryKey, "all">;

/**
 * How a category reads on screen (#553/#554).
 *
 * Plural in the HNWIs band, which names a group ("Business Angels: 6 918");
 * singular on a profile's chip, which names one person ("Business Angel").
 * `ink` is the key of the category's colour in the frontend theme
 * (`palette.category`): the band's dot and the chip share it, so a category
 * has one colour everywhere.
 */
export const PROFILE_CATEGORY_META: Record<
    ProfileCategory,
    { plural: string; singular: string; caption: string; ink: "exit" | "ba" | "fo" | "clevel" }
> = {
    exit_entrepreneurs: { plural: "Exit entrepreneurs", singular: "Exit entrepreneur", caption: "Sold their company", ink: "exit" },
    business_angels: { plural: "Business Angels", singular: "Business Angel", caption: "Individual investors", ink: "ba" },
    family_offices: { plural: "Family Offices", singular: "Family Office", caption: "Single & multi-family", ink: "fo" },
    c_levels: { plural: "C-levels", singular: "C-level", caption: "Senior executives", ink: "clevel" },
};

/** The band's order, which is also the reading order of every legend. */
export const PROFILE_CATEGORIES: readonly ProfileCategory[] = [
    "exit_entrepreneurs",
    "business_angels",
    "family_offices",
    "c_levels",
];

/**
 * The category a stored type label belongs to, or null when it belongs to none
 * (HNWI, a classification rule's own subcategory, an unknown label).
 *
 * The chip shows the category, never the stored label: "FO" and "SFO" both
 * read "Family Office", "BA" and "Business Angels" both read "Business Angel".
 */
export function categoryOfType(label: string | null | undefined): ProfileCategory | null {
    if (!label) return null;
    for (const key of PROFILE_CATEGORIES) {
        if (PROFILE_CATEGORY_TYPES[key].includes(label)) return key;
    }
    return null;
}
